'use client';
import React, { useState } from 'react';

// --- Constants ---
const COLORS = {
  ACCENT_GOLD: '#c69c6d',
  DARK_BG: '#101010',
  LIGHT_TEXT: '#f0f0f0',
};

const fallbackImage = 'https://placehold.co/600x400/374151/ffffff?text=IMAGE+NOT+AVAILABLE';

// --- Data ---
const cafeGalleryData = [
  {
    title: 'CAFÉ INTERIOR',
    subtitle: 'Modern Wellness Design',
    default: '/CafeGallery/interior1.webp',
    hover: '/CafeGallery/interior2.webp',
    alt: 'Interior of the Aura Café with warm golden lighting and cozy seating areas',
    layout: 'tall',
  },
  {
    title: 'HEALTHY CUISINE',
    subtitle: 'Nutritious & Delicious',
    default: '/CafeGallery/food1.webp',
    hover: '/CafeGallery/food2.webp',
    alt: 'Healthy gourmet dishes presented on elegant golden plates',
    layout: 'standard',
  },
  {
    title: 'BARISTA ZONE',
    subtitle: 'Signature Coffee Blends',
    default: '/CafeGallery/barista1.webp',
    hover: '/CafeGallery/barista2.webp',
    alt: 'Barista preparing organic coffee with gold-accented equipment',
    layout: 'standard',
  },
  {
    title: 'LOUNGE SPACE',
    subtitle: 'Relax & Refuel',
    default: '/CafeGallery/lounge1.webp',
    hover: '/CafeGallery/lounge2.webp',
    alt: 'Café lounge with plush seating, plants, and soft lighting',
    layout: 'large',
  },
];

// --- Helpers ---
const handleImageError = (e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = fallbackImage;
  // Optional: add a class or style to indicate error state visually beyond just the image
  e.currentTarget.style.filter = 'grayscale(100%) opacity(50%)';
};

const getLayoutClasses = (layout) => {
    // Using responsive heights: Taller on mobile for better visibility,
    // fixed heights on desktop to ensure the masonry grid aligns perfectly.
  switch (layout) {
    case 'tall':
      // Spans 2 rows on desktop, taller height
      return 'md:col-span-1 md:row-span-2 h-[300px] md:h-[400px]';
    case 'large':
      // Spans 2 columns on desktop, standard height
      return 'md:col-span-2 md:row-span-1 h-[250px] md:h-[200px]';
    case 'standard':
    default:
      // Standard 1x1 block
      return 'md:col-span-1 md:row-span-1 h-[250px] md:h-[200px]';
  }
};

export default function CafeGallery() {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <section className="relative w-full min-h-screen py-16 overflow-hidden font-sans">
      {/* Background Gradient - Cleaned up slightly for readability */}
      <div
        className="absolute inset-0 bg-gradient-to-br opacity-95"
        style={{
            background: `linear-gradient(to bottom right, ${COLORS.DARK_BG}, #262626, #402313, ${COLORS.ACCENT_GOLD}, #a56330, #262626, ${COLORS.DARK_BG})`,
        }}
      ></div>

      {/* Content Container */}
      <div className="relative z-10 max-w-6xl px-4 mx-auto sm:px-6 lg:px-8" style={{ color: COLORS.LIGHT_TEXT }}>
        
        {/* Header */}
        <div className="mb-12 text-center">
          <p
            className="mb-3 text-sm font-bold tracking-[0.2em] uppercase"
            style={{ color: COLORS.ACCENT_GOLD }}
          >
            Café Experience
          </p>
          <h1 className="pb-4 text-4xl font-extrabold tracking-tight drop-shadow-lg sm:text-5xl md:text-6xl">
            Aura <span style={{ color: COLORS.ACCENT_GOLD }}>Cafeteria</span>
          </h1>
          <p className="max-w-2xl mx-auto text-lg text-gray-300 sm:text-xl">
            Discover a fusion of taste, health, and design in our exclusive café gallery.
          </p>
        </div>

        {/* Image Grid - Using standard Tailwind grid classes instead of custom CSS */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 auto-rows-min">
          {cafeGalleryData.map((item, index) => {
            const isHovered = hoveredIndex === index;
            const layoutClasses = getLayoutClasses(item.layout);

            return (
              <div
                key={index}
                className={`relative rounded-xl overflow-hidden cursor-pointer group shadow-2xl transition-all duration-500 ease-out hover:shadow-amber-900/20 hover:-translate-y-1 ${layoutClasses}`}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {/* Image Container for Smooth Transition */}
                <div className="relative w-full h-full">
                    {/* Default Image (Bottom Layer) */}
                    <img
                        src={item.default}
                        alt={item.alt}
                        onError={handleImageError}
                        className="absolute inset-0 object-cover w-full h-full transition-transform duration-700 transform group-hover:scale-110"
                    />
                    
                    {/* Hover Image (Top Layer) - Fades in smoothly */}
                    <img
                        src={item.hover}
                        alt={item.alt}
                        onError={handleImageError}
                        className={`absolute inset-0 object-cover w-full h-full transition-all duration-700 transform group-hover:scale-110 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
                    />
                </div>

                {/* Dark Gradient Overlay (Stronger at bottom for text readability) */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500"></div>

                {/* Text Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-2 transition-transform duration-500 group-hover:translate-y-0">
                  <h2
                    className="text-2xl font-bold leading-tight drop-shadow-md md:text-3xl transition-colors duration-300"
                    style={{
                      color: isHovered ? COLORS.ACCENT_GOLD : COLORS.LIGHT_TEXT,
                    }}
                  >
                    {item.title}
                  </h2>
                  <p
                    className="mt-2 text-xs font-medium tracking-widest uppercase transition-all duration-300 opacity-80 group-hover:opacity-100"
                    style={{ color: COLORS.ACCENT_GOLD }}
                  >
                    {item.subtitle}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}